import Dexie, { type Table } from 'dexie';
import type { AgendaParticipant, AgendaActivityParticipant, AgendaPersonKind, RecurrenceRule } from '@familyhub/shared';
import { registerCleanup } from '../platform/cleanup';
import type { OutboxEntry, MetaRow, RejectionRow } from '../platform/sync-runner';

/**
 * The agenda module's local database — a **separate** IndexedDB database from
 * groceries (`familyhub-agenda` vs `familyhub`), which is the module-independence
 * boundary on the client. Its tables, outbox and cursor never touch the
 * groceries store.
 *
 * Two timelines (docs/AGENDA.md): **activities** (Jurren and/or Olga, never an
 * external person) and **childcare**. Rows are stored exactly as the server
 * sends them, tombstones (`deleted_at`) included and filtered at read time — a
 * tombstone is what propagates a delete to the other phone.
 */

export interface ActivityRecord {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  needs_car: boolean;
  brings_child: boolean;
  notes: string | null;
  participants: AgendaActivityParticipant[];
  recurrence: RecurrenceRule | null;
  created_by: string | null;
  created_at?: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface ChildcareRecord {
  id: string;
  caregivers: AgendaParticipant[];
  starts_at: string;
  ends_at: string | null;
  recurrence: RecurrenceRule | null;
  notes: string | null;
  created_by: string | null;
  created_at?: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface PersonRecord {
  id: string;
  name: string;
  kind: AgendaPersonKind;
  color: string | null;
  created_at?: string;
  updated_at: string;
  deleted_at: string | null;
}

export class AgendaDb extends Dexie {
  activities!: Table<ActivityRecord, string>;
  childcare!: Table<ChildcareRecord, string>;
  people!: Table<PersonRecord, string>;
  outbox!: Table<OutboxEntry, number>;
  rejections!: Table<RejectionRow, string>;
  meta!: Table<MetaRow, string>;

  constructor() {
    super('familyhub-agenda');
    // v1 was the first (rich) model: events / responsibilities / requests.
    this.version(1).stores({
      events: 'id, starts_at, deleted_at',
      responsibilities: 'id, starts_at, deleted_at',
      availability: 'id, user_id, starts_at, deleted_at',
      requests: 'id, starts_at, status, deleted_at',
      people: 'id, deleted_at',
      outbox: '++seq, queued_at',
      rejections: 'id, at',
      meta: 'key',
    });
    // v2 is the simplified two-timeline model (docs/AGENDA.md). The old stores are
    // dropped — this is a greenfield reshape, their rows are not migrated.
    this.version(2)
      .stores({
        events: null,
        responsibilities: null,
        requests: null,
        activities: 'id, starts_at, status, deleted_at',
        childcare: 'id, starts_at, deleted_at',
        availability: 'id, user_id, starts_at, deleted_at',
        people: 'id, deleted_at',
        outbox: '++seq, queued_at',
        rejections: 'id, at',
        meta: 'key',
      })
      .upgrade(async (tx) => {
        // A device that had the old model starts clean on the new one, including
        // its cursor, so the next sync is a full pull of the new tables.
        await tx.table('outbox').clear();
        await tx.table('meta').clear();
      });
    // v3 drops the accept/decline handshake (docs/AGENDA.md §6): `status` and
    // `response_note` are gone from the server's agenda_activities. A full
    // resync overwrites every local row with the new shape anyway, but the
    // stale `status` index would otherwise keep referencing a field that no
    // upsert ever populates again.
    this.version(3)
      .stores({
        activities: 'id, starts_at, deleted_at',
      })
      .upgrade(async (tx) => {
        await tx.table('outbox').clear();
        await tx.table('meta').clear();
      });
    // v4 drops parent "unavailability" (docs/AGENDA.md §3, 2026-08-19 feedback):
    // the household just makes an event instead. A full resync repopulates the
    // remaining tables from the server's new (availability-free) shape.
    this.version(4)
      .stores({
        availability: null,
      })
      .upgrade(async (tx) => {
        await tx.table('outbox').clear();
        await tx.table('meta').clear();
      });
    // v5 (2026-08-20 feedback): childcare gains multiple `caregivers` (replacing
    // the single `caregiver`) and activities gain a `brings_child` flag. No index
    // changes, but clearing the cursor forces a full pull so a device on the old
    // shape never keeps a stale single-`caregiver` row around.
    this.version(5).upgrade(async (tx) => {
      await tx.table('outbox').clear();
      await tx.table('meta').clear();
    });
  }
}

export const adb = new AgendaDb();

/** The Dexie tables the sync runner mirrors, keyed by the server's change bucket. */
export const AGENDA_TABLE_MAP = [
  { name: 'agenda_activities', table: adb.activities as unknown as Table<Record<string, unknown>, string> },
  { name: 'agenda_childcare', table: adb.childcare as unknown as Table<Record<string, unknown>, string> },
  { name: 'agenda_people', table: adb.people as unknown as Table<Record<string, unknown>, string> },
];

export async function getAgendaMeta<T>(key: string): Promise<T | undefined> {
  return (await adb.meta.get(key))?.value as T | undefined;
}
export async function setAgendaMeta(key: string, value: unknown): Promise<void> {
  await adb.meta.put({ key, value });
}

/** Wipe on logout — registered with the platform cleanup registry. */
export async function clearAgendaData(): Promise<void> {
  await Promise.all([
    adb.activities.clear(),
    adb.childcare.clear(),
    adb.people.clear(),
    adb.outbox.clear(),
    adb.rejections.clear(),
    adb.meta.clear(),
  ]);
}

registerCleanup(clearAgendaData);
